import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Flex,
  Grid,
  GridItem,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  IconButton,
} from '@chakra-ui/react';
import { MdSearch, MdDownload, MdDelete } from 'react-icons/md';
import Card from 'components/card/Card';
import {
  getTimeRecords,
  getTimeRecordSummary,
  exportTimeRecords,
  deleteTimeRecord,
} from 'services/timeRecord.service';
import httpClient from '../../../utils/httpClient';

function downloadBase64File({ filename, mimeType, contentBase64 }) {
  const byteCharacters = atob(contentBase64 || '');
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'marcas.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('es-UY', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function TimeRecordReportView() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const toast = useToast();

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .split('T')[0];
  const todayStr = today.toISOString().split('T')[0];

  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(todayStr);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [employees, setEmployees] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    loadData();
  }, [startDate, endDate, employeeFilter, typeFilter, tabIndex]);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const res = await httpClient.get('/employee');
      const emps = res?.payload || res?.data?.payload || [];
      setEmployees(Array.isArray(emps) ? emps : []);
    } catch (error) {
      console.error(error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = { startDate, endDate };
      if (employeeFilter) filters.employeeId = employeeFilter;
      if (typeFilter) filters.recordType = typeFilter;

      if (tabIndex === 0) {
        const res = await getTimeRecords(filters);
        setRecords(res?.payload?.data || []);
      } else {
        const res = await getTimeRecordSummary(filters);
        setSummary(res?.payload?.data || []);
      }
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error al cargar datos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const filters = { startDate, endDate };
      if (employeeFilter) filters.employeeId = employeeFilter;
      if (typeFilter) filters.recordType = typeFilter;
      const res = await exportTimeRecords(filters);
      const payload = res?.payload;
      if (payload?.contentBase64) {
        downloadBase64File(payload);
        toast({ title: 'Reporte descargado', status: 'success', duration: 3000 });
      }
    } catch (error) {
      toast({ title: 'Error al exportar', status: 'error', duration: 3000 });
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta marca?')) return;
    try {
      await deleteTimeRecord(id);
      toast({ title: 'Marca eliminada', status: 'success', duration: 3000 });
      loadData();
    } catch (error) {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000 });
    }
  };

  const filteredRecords = useMemo(() => {
    if (!searchTerm) return records;
    const term = searchTerm.toLowerCase();
    return records.filter(
      (r) =>
        r.employee_name?.toLowerCase().includes(term) ||
        r.employee_document?.includes(term),
    );
  }, [records, searchTerm]);

  const filteredSummary = useMemo(() => {
    if (!searchTerm) return summary;
    const term = searchTerm.toLowerCase();
    return summary.filter(
      (s) =>
        s.employee_name?.toLowerCase().includes(term) ||
        s.employee_document?.includes(term),
    );
  }, [summary, searchTerm]);

  return (
    <Box>
      <Flex justify="flex-end" align="center" mb={6}>
        <Button
          leftIcon={<MdDownload />}
          colorScheme="brand"
          onClick={handleExport}
        >
          Exportar Excel
        </Button>
      </Flex>

      <Card mb={6} p={4}>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
          <GridItem>
            <Text fontSize="sm" mb={1}>Desde</Text>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </GridItem>
          <GridItem>
            <Text fontSize="sm" mb={1}>Hasta</Text>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </GridItem>
          <GridItem>
            <Text fontSize="sm" mb={1}>Funcionario</Text>
            <Select
              placeholder="Todos"
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
            >
              {employees.map((emp) => (
                <option key={emp.id} value={emp.employeeId || emp.id}>
                  {emp.name} {emp.lastName} ({emp.document})
                </option>
              ))}
            </Select>
          </GridItem>
          <GridItem>
            <Text fontSize="sm" mb={1}>Tipo</Text>
            <Select
              placeholder="Todos"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="CHECK_IN">Entrada</option>
              <option value="CHECK_OUT">Salida</option>
            </Select>
          </GridItem>
        </Grid>
        <InputGroup mt={4}>
          <InputLeftElement>
            <Icon as={MdSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Buscar por nombre o documento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </Card>

      <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="brand">
        <TabList>
          <Tab>Detalle de marcas</Tab>
          <Tab>Resumen diario</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            {!loading && (
              <Card overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th borderColor={borderColor}>Fecha/Hora</Th>
                      <Th borderColor={borderColor}>Documento</Th>
                      <Th borderColor={borderColor}>Funcionario</Th>
                      <Th borderColor={borderColor}>Tipo</Th>
                      <Th borderColor={borderColor}>Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredRecords.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" py={8}>
                          <Text color="gray.500">No hay marcas en el período seleccionado</Text>
                        </Td>
                      </Tr>
                    ) : (
                      filteredRecords.map((rec) => (
                        <Tr key={rec.id} _hover={{ bg: rowHover }}>
                          <Td borderColor={borderColor}>{formatDateTime(rec.recorded_at)}</Td>
                          <Td borderColor={borderColor}>{rec.employee_document}</Td>
                          <Td borderColor={borderColor}>{rec.employee_name}</Td>
                          <Td borderColor={borderColor}>
                            <Badge colorScheme={rec.record_type === 'CHECK_IN' ? 'green' : 'red'}>
                              {rec.record_type === 'CHECK_IN' ? 'Entrada' : 'Salida'}
                            </Badge>
                          </Td>
                          <Td borderColor={borderColor}>
                            <IconButton
                              icon={<MdDelete />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              aria-label="Eliminar"
                              onClick={() => handleDelete(rec.id)}
                            />
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Card>
            )}
          </TabPanel>

          <TabPanel px={0}>
            {!loading && (
              <Card overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th borderColor={borderColor}>Fecha</Th>
                      <Th borderColor={borderColor}>Documento</Th>
                      <Th borderColor={borderColor}>Funcionario</Th>
                      <Th borderColor={borderColor}>Entradas</Th>
                      <Th borderColor={borderColor}>Salidas</Th>
                      <Th borderColor={borderColor}>Primera entrada</Th>
                      <Th borderColor={borderColor}>Última salida</Th>
                      <Th borderColor={borderColor}>Horas trabajadas</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredSummary.length === 0 ? (
                      <Tr>
                        <Td colSpan={8} textAlign="center" py={8}>
                          <Text color="gray.500">No hay datos en el período seleccionado</Text>
                        </Td>
                      </Tr>
                    ) : (
                      filteredSummary.map((row, idx) => (
                        <Tr key={`${row.employee_id}_${row.date}_${idx}`} _hover={{ bg: rowHover }}>
                          <Td borderColor={borderColor}>{row.date}</Td>
                          <Td borderColor={borderColor}>{row.employee_document}</Td>
                          <Td borderColor={borderColor}>{row.employee_name}</Td>
                          <Td borderColor={borderColor}>{row.check_ins}</Td>
                          <Td borderColor={borderColor}>{row.check_outs}</Td>
                          <Td borderColor={borderColor}>{formatDateTime(row.first_entry)}</Td>
                          <Td borderColor={borderColor}>{formatDateTime(row.last_exit)}</Td>
                          <Td borderColor={borderColor}>
                            <Badge colorScheme="blue">{row.total_hours}</Badge>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Card>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
